/**
 * EventDetails.tsx
 *
 * Setup Bar — Step 5: Event details.
 *
 * Collects event metadata (name, client, date, attendees, venue, logo) and
 * a free-text prompt enriched by a chip-picker of ~100+ service suggestions.
 *
 * Changes in Sprint 1:
 *  - Bigger pills: 14px font, 10px/16px padding
 *  - Phosphor Icons per category
 *  - ~100+ items across expanded categories:
 *      Technology & Innovation, Activities & Team Building,
 *      Wellness, Registration & Check-in, Signage
 *  - "Recommended for your event" section at top (theme-aware)
 *  - Compact textarea: max-height 120px with overflow-y auto
 *  - Horizontal scrollable category strip with icons + labels
 *  - 20-24 popular chips visible by default in 2 rows
 */

import { useRef, useState, useCallback, useMemo } from 'react';
import {
  MapPin,
  Microphone,
  ForkKnife,
  Camera,
  Confetti,
  FlowerLotus,
  Armchair,
  Package,
  Users,
  Truck,
  Desktop,
  Barbell,
  Heartbeat,
  QrCode,
  ArrowsHorizontal,
  Sparkle,
} from '@phosphor-icons/react';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { Input } from '@/components/forms';
import type { WizardFormData } from '@/types/wizard';
import { StepShell } from '../components';
import styles from './EventDetails.module.css';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];

/**
 * Safety cap (hard truncation) — prevents pathological paste (e.g. entire book).
 * The backend rewrite phase summarizes long input, so anything below this cap
 * is safe; above it, we silently slice. 50k chars ≈ 15 pages.
 */
const PROMPT_HARD_CAP = 50_000;
/**
 * Soft warning threshold — above this we show the user that generation may
 * take longer / cost more. Roughly "more than a page of text".
 */
const PROMPT_WARN_THRESHOLD = 2_000;

// ─── Category metadata ───────────────────────────────────────────────────────

interface CategoryMeta {
  /** Display label for the category filter strip. */
  label: string;
  /** Phosphor icon component to render in the strip. */
  Icon: React.ComponentType<{ size?: number; weight?: 'regular' | 'bold' | 'fill' }>;
}

const CATEGORY_META: Record<string, CategoryMeta> = {
  Venue: { label: 'Venue', Icon: MapPin },
  'AV & Tech': { label: 'AV & Tech', Icon: Microphone },
  Catering: { label: 'Catering', Icon: ForkKnife },
  Entertainment: { label: 'Entertainment', Icon: Confetti },
  Decor: { label: 'Decoration', Icon: FlowerLotus },
  Furniture: { label: 'Furniture', Icon: Armchair },
  Branding: { label: 'Branding', Icon: Package },
  'Photo & Video': { label: 'Foto & Video', Icon: Camera },
  Activities: { label: 'Actividades', Icon: Barbell },
  Logistics: { label: 'Logistics', Icon: Truck },
  Tecnología: { label: 'Technology', Icon: Desktop },
  'Team Building': { label: 'Team Building', Icon: Users },
  Bienestar: { label: 'Wellness', Icon: Heartbeat },
  'Check-in': { label: 'Check-in', Icon: QrCode },
  Señalización: { label: 'Signage', Icon: ArrowsHorizontal },
};

// ─── Theme-to-recommended mappings ──────────────────────────────────────────

/**
 * Maps theme slugs to chip labels recommended for that theme.
 * Used to populate the "Recommended for your event" top rail.
 */
const THEME_RECOMMENDATIONS: Record<string, string[]> = {
  navidad: ['LED Screen', 'Live Band', 'Candy Bar', 'Balloon Arch', 'Neon Sign', 'Photo Booth'],
  corporativo: [
    'Main Stage',
    'Live Streaming',
    'Branded Signage',
    'Welcome Kit',
    'Digital Check-in',
    'Professional Photography',
  ],
  gala: [
    'Red Carpet',
    'VIP Lounge',
    'Plated Dinner',
    'Floral Design',
    'Uplighting',
    'Roaming Camera',
  ],
  outdoor: [
    'Outdoor Terrace',
    'Food Stations',
    'DJ & Sound',
    'Drone Photography',
    'Obstacle Course',
    'Valet Parking',
  ],
  tecnologia: [
    'VR Experience',
    'AI Photo Booth',
    'Hologram Display',
    'Interactive Touchscreen',
    'LED Wristbands',
    'Digital Check-in',
  ],
  deportes: [
    'Obstacle Course',
    'Team Building',
    'Healthy Snack Bar',
    'Drone Photography',
    'Branded Signage',
    'Security',
  ],
};

// ─── Service chip data ───────────────────────────────────────────────────────

interface ServiceChip {
  label: string;
  text: string;
  category: string;
}

const SERVICE_SUGGESTIONS: ServiceChip[] = [
  // Venue & Infrastructure
  { label: 'Entrance Arch', text: '• Entrance arch with event branding and welcome signage', category: 'Venue' },
  { label: 'Main Stage', text: '• Main stage with backdrop, podium, and professional lighting', category: 'Venue' },
  { label: 'VIP Lounge', text: '• Dedicated VIP lounge area with premium seating and exclusive service', category: 'Venue' },
  { label: 'Red Carpet', text: '• Red carpet entrance with step-and-repeat photo wall', category: 'Venue' },
  { label: 'Outdoor Terrace', text: '• Outdoor terrace or garden area for cocktails and networking', category: 'Venue' },

  // Audiovisual & Tech
  { label: 'LED Screen', text: '• Large LED screen/video wall for presentations and live content', category: 'AV & Tech' },
  { label: '360° Camera', text: '• 360° photo/video booth with instant sharing and branded overlays', category: 'AV & Tech' },
  { label: 'Live Streaming', text: '• Professional live streaming setup for remote attendees', category: 'AV & Tech' },
  { label: 'DJ & Sound', text: '• Professional DJ with full sound system and wireless microphones', category: 'AV & Tech' },
  { label: 'AR Experience', text: '• Augmented reality interactive experience stations', category: 'AV & Tech' },
  { label: 'Drone Show', text: '• Indoor or outdoor drone light show', category: 'AV & Tech' },

  // Catering & Drinks
  { label: 'Cocktail Bar', text: '• Open cocktail bar with signature drinks and mixologist', category: 'Catering' },
  { label: 'Food Stations', text: '• Themed food stations (sushi, ceviche, grill, desserts)', category: 'Catering' },
  { label: 'Plated Dinner', text: '• Formal plated dinner service with multi-course menu', category: 'Catering' },
  { label: 'Coffee Corner', text: '• Specialty coffee corner with barista service', category: 'Catering' },
  { label: 'Candy Bar', text: '• Candy bar and dessert table with custom packaging', category: 'Catering' },

  // Entertainment & Shows
  { label: 'Live Band', text: '• Live band or musical act for entertainment', category: 'Entertainment' },
  { label: 'Photo Booth', text: '• Themed photo booth with props, filters, and instant prints', category: 'Entertainment' },
  { label: 'Fireworks', text: '• Pyrotechnic show or cold fireworks finale', category: 'Entertainment' },
  { label: 'Dance Floor', text: '• LED dance floor with intelligent lighting effects', category: 'Entertainment' },
  { label: 'MC / Host', text: '• Professional MC/host to lead the event program', category: 'Entertainment' },
  { label: 'Magician', text: '• Close-up magician or illusionist for table entertainment', category: 'Entertainment' },
  { label: 'Dancers', text: '• Professional dance show (salsa, contemporary, themed)', category: 'Entertainment' },

  // Decoration & Ambiance
  { label: 'Floral Design', text: '• Floral arrangements and centerpieces for tables and stage', category: 'Decor' },
  { label: 'Balloon Arch', text: '• Organic balloon arch and decorative balloon installations', category: 'Decor' },
  { label: 'Neon Sign', text: '• Custom neon sign with event name or hashtag', category: 'Decor' },
  { label: 'Themed Decor', text: '• Full themed decoration package matching the event concept', category: 'Decor' },
  { label: 'Uplighting', text: '• Ambient uplighting and architectural wash lighting', category: 'Decor' },

  // Furniture & Setup
  { label: 'Lounge Furniture', text: '• Lounge furniture sets (sofas, ottomans, cocktail tables)', category: 'Furniture' },
  { label: 'Round Tables', text: '• Round banquet tables for 8-10 guests with chairs and linens', category: 'Furniture' },
  { label: 'Cocktail Tables', text: '• High cocktail tables for standing reception', category: 'Furniture' },

  // Branding & Merch
  { label: 'Welcome Kit', text: '• Branded welcome kit/swag bag for each attendee', category: 'Branding' },
  { label: 'Branded Signage', text: '• Custom branded signage, banners, and directional signs', category: 'Branding' },
  { label: 'Photo Wall', text: '• Branded photo wall / selfie station with event hashtag', category: 'Branding' },

  // Photography & Video
  { label: 'Professional Photography', text: '• Professional event photographer (full coverage)', category: 'Photo & Video' },
  { label: 'Video Highlight', text: '• Same-day video highlight reel / after-movie production', category: 'Photo & Video' },
  { label: 'Roaming Camera', text: '• Roaming camera crew capturing candid moments', category: 'Photo & Video' },

  // Activities
  { label: 'Team Building', text: '• Team building activities and group dynamics facilitation', category: 'Activities' },
  { label: 'Trivia / Quiz', text: '• Interactive trivia or quiz game with prizes', category: 'Activities' },
  { label: 'Karaoke', text: '• Karaoke station with professional audio setup', category: 'Activities' },
  { label: 'Casino Night', text: '• Casino tables (poker, blackjack, roulette) with play chips', category: 'Activities' },

  // Logistics & Staff
  { label: 'Valet Parking', text: '• Valet parking service for guests', category: 'Logistics' },
  { label: 'Security', text: '• Security personnel and access control', category: 'Logistics' },
  { label: 'Hostesses', text: '• Protocol hostesses for guest registration and guidance', category: 'Logistics' },
  { label: 'Transport', text: '• Guest transportation / shuttle service', category: 'Logistics' },

  // Tecnología e Innovación
  { label: 'VR Experience', text: '• Virtual reality experience stations with branded headsets', category: 'Tecnología' },
  { label: 'Hologram Display', text: '• Holographic display or projection pyramid for product reveals', category: 'Tecnología' },
  { label: 'AI Photo Booth', text: '• AI-powered photo booth with real-time filters and art generation', category: 'Tecnología' },
  { label: 'Interactive Touchscreen', text: '• Large interactive touchscreen panels for content navigation', category: 'Tecnología' },
  { label: 'LED Wristbands', text: '• Programmable LED wristbands for synchronized light shows', category: 'Tecnología' },
  { label: 'Drone Photography', text: '• Aerial drone photography and video coverage', category: 'Tecnología' },

  // Team Building
  { label: 'Cooking Class', text: '• Live cooking class or culinary challenge with chef', category: 'Team Building' },
  { label: 'Wine Tasting', text: '• Guided wine or cocktail tasting experience', category: 'Team Building' },
  { label: 'Escape Room', text: '• Custom themed escape room experience for groups', category: 'Team Building' },
  { label: 'Obstacle Course', text: '• Outdoor or indoor obstacle / adventure course', category: 'Team Building' },
  { label: 'Art Workshop', text: '• Collaborative art or craft workshop with facilitator', category: 'Team Building' },
  { label: 'Scavenger Hunt', text: '• City-wide or venue scavenger hunt with prizes', category: 'Team Building' },

  // Bienestar
  { label: 'Meditation Corner', text: '• Quiet meditation and mindfulness corner with guided sessions', category: 'Bienestar' },
  { label: 'Massage Station', text: '• On-site chair massage station for attendees', category: 'Bienestar' },
  { label: 'Yoga Session', text: '• Morning or evening yoga / stretching session', category: 'Bienestar' },
  { label: 'Healthy Snack Bar', text: '• Wellness snack bar with fresh juices, smoothies, and healthy bites', category: 'Bienestar' },

  // Registro & Check-in
  { label: 'Digital Check-in', text: '• Digital self check-in kiosks with QR validation', category: 'Check-in' },
  { label: 'QR Badges', text: '• Personalized QR-coded event badges for each attendee', category: 'Check-in' },
  { label: 'Welcome Desk', text: '• Branded welcome desk with staffed concierge service', category: 'Check-in' },
  { label: 'Gift Bag Station', text: '• Gift bag assembly and distribution station at entry', category: 'Check-in' },

  // Señalización
  { label: 'Directional Signs', text: '• Branded directional signage throughout the venue', category: 'Señalización' },
  { label: 'Menu Boards', text: '• Printed or digital menu boards at food and bar stations', category: 'Señalización' },
  { label: 'Event App', text: '• Custom event mobile app with schedule, maps, and networking', category: 'Señalización' },
  { label: 'Schedule Display', text: '• Large format schedule display at venue entrance', category: 'Señalización' },
  { label: 'Floor Graphics', text: '• Custom floor graphics and wayfinding decals', category: 'Señalización' },
];

/** Default "Popular" picks — the first 24 chips shown before any category filter. */
const POPULAR_CHIP_LABELS = new Set([
  'LED Screen', 'Live Band', 'Photo Booth', 'Cocktail Bar', 'Food Stations',
  'DJ & Sound', 'Floral Design', 'Balloon Arch', 'Welcome Kit', 'Professional Photography',
  'Team Building', 'Live Streaming', 'Branded Signage', 'Entrance Arch', 'Main Stage',
  'VIP Lounge', 'Candy Bar', 'MC / Host', 'Fireworks', 'Drone Photography',
  'Digital Check-in', 'QR Badges', 'VR Experience', 'AI Photo Booth',
]);

const CHIP_CATEGORIES = [...new Set(SERVICE_SUGGESTIONS.map((s) => s.category))];

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  formData: WizardFormData;
  updateForm: <K extends keyof WizardFormData>(key: K, value: WizardFormData[K]) => void;
  onGenerate: () => void;
}

export const EventDetails = ({ formData, updateForm, onGenerate }: Props) => {
  const canGenerate =
    formData.event_name.trim().length > 0 && formData.client_name.trim().length > 0;
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [activeChipCategory, setActiveChipCategory] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const appendService = (text: string) => {
    const current = formData.prompt.trim();
    const separator = current ? '\n' : '';
    updateForm('prompt', current + separator + text);
  };

  /**
   * Silent hard cap — prevents absurd payloads from reaching the API.
   * Soft warning is shown in the UI well before this point.
   */
  const setPromptSafe = (v: string) => {
    updateForm('prompt', v.length > PROMPT_HARD_CAP ? v.slice(0, PROMPT_HARD_CAP) : v);
  };

  const promptLength = formData.prompt.length;
  const isLongPrompt = promptLength > PROMPT_WARN_THRESHOLD;

  const isServiceAdded = useCallback(
    (text: string) => formData.prompt.includes(text),
    [formData.prompt],
  );

  /** Chips visible in the main grid (popular or category-filtered). */
  const visibleChips = useMemo(() => {
    if (!activeChipCategory) {
      return SERVICE_SUGGESTIONS.filter((s) => POPULAR_CHIP_LABELS.has(s.label));
    }
    return SERVICE_SUGGESTIONS.filter((s) => s.category === activeChipCategory);
  }, [activeChipCategory]);

  /** Theme-aware recommended chips shown at the top of the chip picker. */
  const recommendedLabels = useMemo(() => {
    const matched = formData.theme_slugs
      .flatMap((slug) => THEME_RECOMMENDATIONS[slug] ?? [])
      .filter((label) => !isServiceAdded(SERVICE_SUGGESTIONS.find((s) => s.label === label)?.text ?? ''));
    // Deduplicate, cap at 6
    return [...new Set(matched)].slice(0, 6);
  }, [formData.theme_slugs, isServiceAdded]);

  const handleLogoUpload = useCallback(
    async (file: File) => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return;
      setIsUploadingLogo(true);
      try {
        const form = new FormData();
        form.append('files', file);
        const res = await api.post(API_ROUTES.uploadProviderImage, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const uploaded = res.data?.uploaded as Array<{ url: string }> | undefined;
        if (uploaded && uploaded.length > 0) {
          updateForm('logo_url', uploaded[0].url);
        }
      } catch {
        // silent — logo upload failure is non-blocking
      } finally {
        setIsUploadingLogo(false);
      }
    },
    [updateForm],
  );

  const handleLogoDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = Array.from(e.dataTransfer.files).find((f) =>
        ACCEPTED_IMAGE_TYPES.includes(f.type),
      );
      if (file) handleLogoUpload(file);
    },
    [handleLogoUpload],
  );

  return (
    <StepShell
      title="Event Details"
      subtitle="Just a few details to personalize your presentation"
      nextLabel="Generate Presentation"
      onNext={onGenerate}
      nextDisabled={!canGenerate || isUploadingLogo}
    >
      <div className={styles.form}>
        <div className={styles.row}>
          <Input
            name="event_name"
            label="Event Name"
            value={formData.event_name}
            setValue={(v) => updateForm('event_name', v)}
            placeholder="e.g. Corporate Christmas Party 2026"
            autoComplete="on"
          />
          <Input
            name="client_name"
            label="Client Name"
            value={formData.client_name}
            setValue={(v) => updateForm('client_name', v)}
            placeholder="e.g. Acme Corp"
            autoComplete="organization"
          />
        </div>

        {/* Logo upload */}
        <div className={styles.logoSection}>
          <label className={styles.logoLabel}>Client / Company Logo</label>
          <p className={styles.logoHint}>
            Upload your client's logo to include it on the presentation.
          </p>

          {formData.logo_url ? (
            <div className={styles.logoPreview}>
              <img src={formData.logo_url} alt="Logo" className={styles.logoImg} />
              <button
                type="button"
                className={styles.logoRemove}
                onClick={() => updateForm('logo_url', '')}
                aria-label="Remove logo"
              >
                &times;
              </button>
            </div>
          ) : (
            <div
              className={`${styles.logoDropzone} ${isUploadingLogo ? styles.logoUploading : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleLogoDrop}
              onClick={() => !isUploadingLogo && logoInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') logoInputRef.current?.click();
              }}
              aria-label="Upload logo"
            >
              <input
                ref={logoInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                className={styles.hiddenInput}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleLogoUpload(file);
                  if (e.target) e.target.value = '';
                }}
                disabled={isUploadingLogo}
              />
              {isUploadingLogo ? (
                <span className={styles.logoDropzoneText}>Uploading...</span>
              ) : (
                <>
                  <span className={styles.logoDropzoneIcon}>&#127912;</span>
                  <span className={styles.logoDropzoneText}>
                    Drag your logo here or <strong>click to select</strong>
                  </span>
                  <span className={styles.logoDropzoneHint}>PNG, JPG, WebP, or SVG</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Prompt + chip picker */}
        <div className={styles.promptSection}>
          <Input
            name="prompt"
            label="Additional Instructions (Optional)"
            value={formData.prompt}
            setValue={setPromptSafe}
            placeholder="Click services below to add them, or type your own requirements..."
            multiline
            rows={4}
          />
          {isLongPrompt && (
            <p className={styles.promptWarning}>
              {promptLength.toLocaleString()} characters — long instructions are fine, but
              generation may take longer and cost more tokens. We'll automatically summarize
              them before planning the deck.
            </p>
          )}

          <div className={styles.suggestions}>
            <span className={styles.suggestionsLabel}>Add services &amp; features:</span>

            {/* Recommended chips (theme-aware) */}
            {recommendedLabels.length > 0 && (
              <div className={styles.recommendedSection}>
                <span className={styles.recommendedLabel}>
                  <Sparkle size={12} weight="fill" /> Recommended for your event
                </span>
                <div className={styles.suggestionsChips}>
                  {recommendedLabels.map((label) => {
                    const chip = SERVICE_SUGGESTIONS.find((s) => s.label === label);
                    if (!chip) return null;
                    const added = isServiceAdded(chip.text);
                    return (
                      <button
                        key={chip.label}
                        type="button"
                        className={`${styles.suggestionChip} ${styles.suggestionChipRecommended} ${added ? styles.suggestionChipAdded : ''}`}
                        onClick={() => !added && appendService(chip.text)}
                        disabled={added}
                      >
                        {added ? '✓ ' : '+ '}
                        {chip.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Category filter strip — horizontally scrollable */}
            <div className={styles.categoryStrip}>
              <button
                type="button"
                className={`${styles.categoryChip} ${!activeChipCategory ? styles.categoryChipActive : ''}`}
                onClick={() => setActiveChipCategory(null)}
              >
                <Sparkle size={13} />
                Popular
              </button>
              {CHIP_CATEGORIES.map((cat) => {
                const meta = CATEGORY_META[cat];
                const CatIcon = meta?.Icon;
                return (
                  <button
                    key={cat}
                    type="button"
                    className={`${styles.categoryChip} ${activeChipCategory === cat ? styles.categoryChipActive : ''}`}
                    onClick={() => setActiveChipCategory((prev) => (prev === cat ? null : cat))}
                  >
                    {CatIcon && <CatIcon size={13} />}
                    {meta?.label ?? cat}
                  </button>
                );
              })}
            </div>

            {/* Chip grid */}
            <div className={styles.suggestionsChips}>
              {visibleChips.map((s) => {
                const added = isServiceAdded(s.text);
                return (
                  <button
                    key={s.label}
                    type="button"
                    className={`${styles.suggestionChip} ${added ? styles.suggestionChipAdded : ''}`}
                    onClick={() => !added && appendService(s.text)}
                    disabled={added}
                  >
                    {added ? '✓ ' : '+ '}
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </StepShell>
  );
};
